/// <reference path="../../Scripts/typings/global.d.ts" />
import { gpCommon } from '../app';
import { ISimpleHttpService } from './authorized-http-service';
import { IAuthorisationCheckerService, IAuthorisationCheckerService_serviceName } from './authorisation-checker-with-caching-service';
import { dayjs } from '#gp/app-common/util/dayjs';
import {
    ActivateSubscriptionResponse,
    AssignLicenseRequest,
    AssignLicenseResponse,
    BillingInfo,
    CheckLicenseReassignmentRequest,
    CheckLicenseReassignmentResponse,
    PaymentMethod,
    Plan as Model_Plan,
    RegisterPaymentMethodResponse,
    StripeInvoiceSummary,
    Subscription,
    SubscriptionInfo,
    SubscriptionStatus,
    UserSettings,
    InvoiceEmailPreferenceResponse,
    InvoiceEmailPreferenceRequest,
    InvoiceFromServer
} from './subscription.models';
import { gpSetCapabilities } from '../util/capabilities';
import { emailChangeRefusalMessage } from './email-change-refusal';

export type ISubscriptionStatus = SubscriptionStatus;
export type IRegisterPaymentMethodResponse = RegisterPaymentMethodResponse;
export type IActivateSubscriptionResponse = ActivateSubscriptionResponse;
export type IInvoiceFromServer = InvoiceFromServer;
export type ISubscriptionInfo = SubscriptionInfo;
export type ISubscription = Subscription;
export type IUserSettings = UserSettings;
export type IBillingInfo = BillingInfo;
export type IPaymentMethod = PaymentMethod;
export type Plan = Model_Plan;

/** The self-only api/accounts/user/{id} response (SimpleQ10User, camelCased). */
export interface CurrentUserInfo {
    id: string;
    userName: string;
    name: string;
    email: string;
}

export class CurrentUserService {

    static readonly serviceName = 'gpCurrentUserService';

    static $inject = [
        '$http',
        '$httpAuth',
        '$q',
        IAuthorisationCheckerService_serviceName,
    ];

    private serviceBase = __gameplan_service_url || 'http://localhost/';
    private lastGetSubscriptionStatus: {
        timestamp: dayjs.Dayjs;
        promise: ng.IPromise<ISubscriptionStatus>;
    } | null = null;
    handlers: { name: string; handler: () => void; }[] = [];

    constructor(
        private readonly $httpOld: ng.IHttpService,
        private readonly $http: ISimpleHttpService,
        private readonly $q: ng.IQService,
        private readonly authService: IAuthorisationCheckerService) {
    }

    public getSubscriptionStatus(secondsSinceLastCheck: number = 0): ng.IPromise<ISubscriptionStatus> {
        const now = dayjs();
        if (this.lastGetSubscriptionStatus && now.isBefore(this.lastGetSubscriptionStatus.timestamp.add(secondsSinceLastCheck, 'seconds'))) {
            return this.lastGetSubscriptionStatus.promise;
        }

        const deferred = this.$q.defer<ISubscriptionStatus>();

        this.lastGetSubscriptionStatus = {
            promise: deferred.promise,
            timestamp: dayjs(),
        }

        const authReq = this.authService.checkAuthorisationIfNeeded();

        authReq.then(authData => {
            const hget = this.$httpOld.get<ISubscriptionStatus>(this.serviceBase + 'api/accounts/subscriptionstatus', {
                headers: { 'Authorization': 'bearer ' + authData.token, }
            });

            hget.then(result => deferred.resolve(result.data)).catch(err => { });
            hget.catch(err => deferred.reject(err));
        }).catch(err => { });

        authReq.catch(reason => deferred.reject(reason));

        deferred.promise.then(status => {
            // Refill the capability snapshot (issue #563). This response is
            // the transport; the page render seeds the same snapshot, so a
            // gate asked before this resolves already has a real answer.
            gpSetCapabilities(status.capabilities);
        }).catch(err => { });

        return deferred.promise;
    }

    registerPaymentMethod(tokenId: string): ng.IPromise<IRegisterPaymentMethodResponse> {
        const deferred = this.$q.defer<IRegisterPaymentMethodResponse>();
        const authReq = this.authService.checkAuthorisationIfNeeded();

        authReq.then(authData => {
            const hget = this.$httpOld.post<IRegisterPaymentMethodResponse>(
                this.serviceBase + 'api/accounts/paymentmethods',
                { tokenId: tokenId },
                { headers: { 'Authorization': 'bearer ' + authData.token, } });

            hget.then(result => deferred.resolve(result.data)).catch(err => { });
            hget.catch(err => deferred.reject(err));
        }).catch(err => { });

        authReq.catch(reason => deferred.reject(reason));

        return deferred.promise;
    }

    activateSubscription(planId: string): ng.IPromise<IActivateSubscriptionResponse> {
        const deferred = this.$q.defer<IActivateSubscriptionResponse>();
        const authReq = this.authService.checkAuthorisationIfNeeded();

        authReq.then(authData => {
            const hget = this.$httpOld.post<IActivateSubscriptionResponse>(this.serviceBase + 'api/accounts/subscriptions',
                { planId: planId },
                { headers: { 'Authorization': 'bearer ' + authData.token, } });

            hget.then(result => deferred.resolve(result.data)).catch(err => { });
            hget.catch(err => deferred.reject(err));
        }).catch(err => { });

        authReq.catch(reason => deferred.reject(reason));

        return deferred.promise;
    }

    /** Resolves with the message to show on success; rejects with the one to show as an error. */
    requestEmailChange(oldEmail: string, newEmail: string, currentPassword: string): ng.IPromise<string> {
        const deferred = this.$q.defer<string>();

        if (newEmail) {
            const req = this.$http.post(this.serviceBase + 'api/accounts/user/request-email-change/', {
                oldEmail: oldEmail,
                newEmail: newEmail,
                currentPassword: currentPassword,
            });

            req.then(() => deferred.resolve("We have just sent you an email to verify your new email address")).catch(err => { });
            req.catch(err => deferred.reject(emailChangeRefusalMessage(err)));
        } else {
            deferred.reject("No email address provided");
        }

        return deferred.promise;
    }

    getInvoices(): ng.IPromise<IInvoiceFromServer[]> {
        const deferred = this.$q.defer<IInvoiceFromServer[]>();

        const req = this.$http.get<IInvoiceFromServer[]>(this.serviceBase + 'api/invoices');

        req.then(response => deferred.resolve(response.data)).catch(err => { });
        req.catch(reason => deferred.reject(reason));

        return deferred.promise;
    }

    payInvoice(invoiceNumber: string, paymentToken: string): ng.IPromise<any> {
        const deferred = this.$q.defer<IInvoiceFromServer[]>();

        const req = this.$http.post<IInvoiceFromServer[]>(this.serviceBase + 'api/invoices/' + invoiceNumber + '/payment', {
            token: paymentToken,
        });

        req.then(response => deferred.resolve(response.data)).catch(err => { });
        req.catch(reason => deferred.reject(reason));

        req.finally(() => this.notify('invoicesChanged'));

        return deferred.promise;
    }


    checkSubscriptionPayment(): ng.IPromise<ISubscriptionInfo> {
        const deferred = this.$q.defer<ISubscriptionInfo>();
        const req = this.$http.get<ISubscriptionInfo>(this.serviceBase + 'api/subscription/check');

        req.then(response => deferred.resolve(response.data)).catch(err => { });
        req.catch(reason => deferred.reject(reason));

        return deferred.promise;

    }

    getSubscriptionInfo(): ng.IPromise<ISubscriptionInfo> {
        const deferred = this.$q.defer<ISubscriptionInfo>();
        const req = this.$http.get<ISubscriptionInfo>(this.serviceBase + 'api/subscription/info');

        req.then(response => deferred.resolve(response.data)).catch(err => { });
        req.catch(reason => deferred.reject(reason));

        return deferred.promise;
    }



    getCustomerPortalLink(whatToManage: string): ng.IPromise<{ url: string, error: string }> {
        const deferred = this.$q.defer<{ url: string, error: string }>();
        const url = this.serviceBase + 'api/subscription/get-customer-portal-link?whatToManage=' + encodeURIComponent(whatToManage);
        const req = this.$http.get<{ url: string, error: string }>(url);

        req.then(response => deferred.resolve(response.data)).catch(err => { });
        req.catch(reason => deferred.reject(reason));

        return deferred.promise;
    }

    checkLicenseReassignment(request: CheckLicenseReassignmentRequest): ng.IPromise<CheckLicenseReassignmentResponse> {
        const deferred = this.$q.defer<CheckLicenseReassignmentResponse>();
        const req = this.$http.post<CheckLicenseReassignmentResponse>(this.serviceBase + 'api/subscription/check-license-reassignment', request);

        req.then(response => deferred.resolve(response.data)).catch(err => { });
        req.catch(reason => deferred.reject(reason));

        return deferred.promise;
    }

    assignLicense(request: AssignLicenseRequest): ng.IPromise<AssignLicenseResponse> {
        const deferred = this.$q.defer<AssignLicenseResponse>();
        const req = this.$http.post<AssignLicenseResponse>(this.serviceBase + 'api/subscription/assign-license', request);

        req.then(response => deferred.resolve(response.data)).catch(err => { });
        req.catch(reason => deferred.reject(reason));

        return deferred.promise;
    }


    /** The signed-in user's own record (SimpleQ10User, self-only endpoint). */
    getCurrentUserInfo(): ng.IPromise<CurrentUserInfo> {
        const deferred = this.$q.defer<CurrentUserInfo>();

        const authReq = this.authService.checkAuthorisationIfNeeded();

        authReq.then(authStatus => {
            const req = this.$http.get<CurrentUserInfo>(this.serviceBase + `api/accounts/user/${authStatus.userId}`);

            req.then(response => deferred.resolve(response.data)).catch(err => { });
            req.catch(reason => deferred.reject(reason));
        }).catch(err => {
            deferred.reject(err)
        });

        return deferred.promise;
    }

    getUserSettings(): ng.IPromise<IUserSettings> {
        const deferred = this.$q.defer<IUserSettings>();

        const authReq = this.authService.checkAuthorisationIfNeeded();

        authReq.then(authStatus => {
            const req = this.$http.get<IUserSettings>(this.serviceBase + `api/accounts/user/${authStatus.userId}/settings`);

            req.then(response => deferred.resolve(response.data)).catch(err => { });
            req.catch(reason => deferred.reject(reason));
        }).catch(err => {
            deferred.reject(err)
        });

        return deferred.promise;
    }

    saveUserSettings(newSettings: IUserSettings) {
        const deferred = this.$q.defer<string>();

        const authReq = this.authService.checkAuthorisationIfNeeded();

        authReq.then(authStatus => {
            const req = this.$http.post<string>(this.serviceBase + `api/accounts/user/${authStatus.userId}/settings`, newSettings);

            req.then(response => deferred.resolve(response.data)).catch(err => { });
            req.catch(reason => deferred.reject(reason));
        }).catch(err => {
            deferred.reject(err)
        });

        return deferred.promise;

    }

    getUrlForSubscriptionChange(planCode: string, referrerUrl: string): ng.IPromise<string> {
        const deferred = this.$q.defer<string>();

        const req = this.$http.get<string>(this.serviceBase + 'api/subscription/change', {
            params: {
                planCode: planCode,
                referrerUrl: referrerUrl,
            }
        });

        req.then(response => deferred.resolve(response.data)).catch(err => { });
        req.catch(reason => deferred.reject(reason));

        return deferred.promise;
    }

    sendFeedback(formData: any): ng.IPromise<string> {
        const deferred = this.$q.defer<string>();

        const req = this.$http.post<string>(this.serviceBase + 'api/feedback/send', {
            formData: formData,
        });

        req.then(response => deferred.resolve(response.data)).catch(err => { });
        req.catch(reason => deferred.reject(reason));

        return deferred.promise;
    }

    cancelSubscription(formData: any): ng.IPromise<string> {
        const deferred = this.$q.defer<string>();

        const req = this.$http.post<string>(this.serviceBase + 'api/subscription/cancel-subscription', {
            formData: formData,
        });

        req.then(response => deferred.resolve(response.data)).catch(err => { });
        req.catch(reason => deferred.reject(reason));

        return deferred.promise;
    }

    getSubscriptionInvoices(subscriptionId: string): ng.IPromise<StripeInvoiceSummary[]> {
        const deferred = this.$q.defer<StripeInvoiceSummary[]>();
        const req = this.$http.get<StripeInvoiceSummary[]>(this.serviceBase + 'api/subscription/' + encodeURIComponent(subscriptionId) + '/invoices');

        req.then(response => deferred.resolve(response.data)).catch(err => { });
        req.catch(reason => deferred.reject(reason));

        return deferred.promise;
    }

    getInvoiceEmailPreference(subscriptionId: string): ng.IPromise<InvoiceEmailPreferenceResponse> {
        const deferred = this.$q.defer<InvoiceEmailPreferenceResponse>();
        const req = this.$http.get<InvoiceEmailPreferenceRequest>(this.serviceBase + 'api/subscription/' + encodeURIComponent(subscriptionId) + '/email-preference');

        req.then(response => deferred.resolve(response.data)).catch(err => { });
        req.catch(reason => deferred.reject(reason));

        return deferred.promise;
    }

    setInvoiceEmailPreference(subscriptionId: string, sendInvoiceEmails: boolean): ng.IPromise<InvoiceEmailPreferenceResponse> {
        const deferred = this.$q.defer<InvoiceEmailPreferenceResponse>();
        const req = this.$http.post<InvoiceEmailPreferenceRequest>(this.serviceBase + 'api/subscription/' + encodeURIComponent(subscriptionId) + '/email-preference', {
            sendInvoiceEmails: sendInvoiceEmails,
        });

        req.then(response => deferred.resolve(response.data)).catch(err => { });
        req.catch(reason => deferred.reject(reason));

        return deferred.promise;
    }

    reactivateSubscription(): ng.IPromise<string> {
        const deferred = this.$q.defer<string>();

        let req = this.$http.post<string>(this.serviceBase + 'api/subscription/reactivate-subscription', {});

        req.then(response => deferred.resolve(response.data)).catch(err => { });
        req.catch(reason => deferred.reject(reason));

        return deferred.promise;
    }

    on(eventName: string, handler: () => void) {
        this.handlers.push({ name: eventName, handler: handler });
    }

    off(eventName: string, handler: () => void) {
        for (let i = this.handlers.length - 1; i >= 0; --i) {
            if (this.handlers[i].name === eventName && this.handlers[i].handler === handler) {
                this.handlers.splice(i, 1);
            }
        }
    }

    notify(eventName: string) {
        this.handlers.forEach(h => {
            if (h.name === eventName) {
                h.handler();
            }
        });
    }
}

gpCommon.service(CurrentUserService.serviceName, CurrentUserService);
